import React, { useState, useEffect } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import AdminTable from "../../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTablePagination from "../../../components/adminDashboard/AdminTable/AdminTablePagination";
import AdminRosterManagementTable from "../../../components/adminDashboard/AdminRosterManagementTable";
import InviteStudentModalContainer from "../../courseSettings/RosterManagementContainer/InviteStudentModalContainer";
import {
  useGetStudentsByCourseIdQuery,
  useInviteUsersToEnrollInCourseMutation,
  useRequestRosterExportMutation,
  useGetRosterExportFileQuery,
  useRemoveStudentsFromCourseMutation,
} from "@axio/graphql/generated/graphql";
import usePagination from "../../../hooks/usePagination";
import useSortingDropdown from "../../../hooks/useSortingDropdown";
import { Plus } from "@phosphor-icons/react/dist/csr/Plus";
import { DownloadSimple } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { useDebounce } from "use-debounce";
import useFileFetch from "../../../hooks/useFileFetch";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
];

interface AdminRosterManagementContainerProps {
  courseId: string;
}

const AdminRosterManagementContainer: React.FC<AdminRosterManagementContainerProps> = ({ courseId }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchValue] = useDebounce(searchQuery, 500);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState({
    visible: false,
    title: "",
    message: "",
    color: "#111827",
  });

  const [removeModalState, setRemoveModalState] = useState({
    visible: false,
    studentId: "",
    studentName: "",
  });

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const {
    data: studentsData,
    loading,
    error,
    refetch: studentsRefetch,
  } = useGetStudentsByCourseIdQuery({
    variables: {
      courseId,
      searchQuery: debouncedSearchValue,
      page,
      pageSize,
      sortBy,
    },
    skip: !courseId,
  });

  const students = studentsData?.getStudentsByCourseId?.data ?? [];
  const totalCount = studentsData?.getStudentsByCourseId?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const [inviteUsersToEnrollInCourse] = useInviteUsersToEnrollInCourseMutation();
  const [removeStudentsFromCourse] = useRemoveStudentsFromCourseMutation();

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    studentsRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleInviteStudents = async (studentsToInvite: { name: string; email: string }[]) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUsersToEnrollInCourse({
        variables: {
          input: {
            courseId,
            users: studentsToInvite,
          },
        },
      });

      if (result.data?.inviteUsersToEnrollInCourse.success) {
        studentsRefetch();
        setIsInviteModalVisible(false);
        setIsSubmitting(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    } catch (error) {
      console.error("Error inviting students:", error);
      setIsSubmitting(false);
      return false;
    }
  };
  const [requestRosterExport] = useRequestRosterExportMutation();
  const { data: rosterExportFileData, refetch: fetchLatestRosterExport } = useGetRosterExportFileQuery({
    variables: { courseId },
    skip: !courseId,
  });

  const [exportStatus, setExportStatus] = useState({
    isGenerating: false,
    hasExport: false,
    fileURL: "",
  });

  const handleExportRoster = async () => {
    try {
      setExportStatus((prev) => ({ ...prev, isGenerating: true, hasExport: false }));

      const res = await requestRosterExport({ variables: { courseId } });
      if (res?.data?.requestRosterExport?.success) {
        setTimeout(fetchLatestRosterExport, 2000);
      } else {
        throw new Error(res?.data?.requestRosterExport?.message || "Failed to start roster export");
      }
    } catch (err) {
      console.error("Error starting roster export:", err);
      setExportStatus((prev) => ({ ...prev, isGenerating: false }));
    }
  };

  useEffect(() => {
    if (rosterExportFileData?.getRosterExportFile?.success) {
      const file = rosterExportFileData.getRosterExportFile.data;
      setExportStatus({
        isGenerating: false,
        hasExport: true,
        fileURL: file.fileURL ?? "",
      });
    } else {
      setExportStatus({
        isGenerating: false,
        hasExport: false,
        fileURL: "",
      });
    }
  }, [rosterExportFileData]);

  const { fetchAndOpenFile } = useFileFetch();

  const handleDownloadExport = async () => {
    if (exportStatus.fileURL) {
      await fetchAndOpenFile(exportStatus.fileURL);
    }
  };

  const handleResendInvite = async (studentName: string, studentEmail: string) => {
    try {
      const result = await inviteUsersToEnrollInCourse({
        variables: {
          input: {
            courseId,
            users: [{ name: studentName, email: studentEmail }],
          },
        },
      });

      if (result.data?.inviteUsersToEnrollInCourse.success) {
        setModalState({
          visible: true,
          title: "Message Sent",
          message: "The invitation has been resent successfully.",
          color: "#111827",
        });
        setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
      }
    } catch (error) {
      console.error("Error resending invite:", error);
      setModalState({
        visible: true,
        title: "Error",
        message: "Failed to resend the invitation. Please try again.",
        color: "#DC2626",
      });
      setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
    }
  };

  const handleRemoveFromCourse = (studentId: string, studentName: string) => {
    setRemoveModalState({
      visible: true,
      studentId,
      studentName,
    });
  };

  const confirmRemoveFromCourse = async () => {
    try {
      const result = await removeStudentsFromCourse({
        variables: {
          courseId,
          studentIds: [removeModalState.studentId],
        },
      });

      if (result.data?.removeStudentsFromCourse.success) {
        studentsRefetch();
        setRemoveModalState({ visible: false, studentId: "", studentName: "" });
        setModalState({
          visible: true,
          title: "Student Removed",
          message: `${removeModalState.studentName} has been removed from the course.`,
          color: "#111827",
        });
        setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
      }
    } catch (error) {
      console.error("Error removing student:", error);
      setModalState({
        visible: true,
        title: "Error",
        message: "Failed to remove the student. Please try again.",
        color: "#DC2626",
      });
      setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
    }
  };

  // if (loading) {
  //   return (
  //     <View style={styles.loaderContainer}>
  //       <ActivityIndicator size="large" color="#0000ff" />
  //     </View>
  //   );
  // }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading students" size="m" useTranslationText={false} />
      </View>
    );
  }
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CustomText
            text={`Enrolled Students (${loading ? "...loading" : totalCount})`}
            size="l"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
          />
        </View>
        <View style={styles.headerRight}>
          <View style={styles.buttonGroup}>
            {exportStatus.isGenerating ? (
              <CustomButton
                onPress={null}
                text="Generating..."
                size="s"
                weight={700}
                styleType="primaryLight"
                disabled={true}
              />
            ) : exportStatus.hasExport ? (
              <View style={{ flexDirection: "row", gap: 8 }}>
                <CustomButton
                  text="Download Export"
                  size="s"
                  weight={700}
                  styleType="primaryLight"
                  onPress={handleDownloadExport}
                />
                <CustomButton
                  text="Regenerate"
                  size="s"
                  weight={700}
                  styleType="primaryLight"
                  onPress={handleExportRoster}
                />
              </View>
            ) : (
              <CustomButton
                text="Export Roster"
                styleType="primaryLight"
                size="m"
                leftIcon={<DownloadSimple size={24} color="#3E68FE" />}
                onPress={handleExportRoster}
                useTranslationText={false}
                style={styles.exportButton}
                textStyle={{ color: "#3E68FE", fontWeight: "700", fontSize: 14 }}
              />
            )}
          </View>
          <CustomButton
            text="Add Student"
            styleType="primary"
            size="m"
            leftIcon={<Plus size={24} color="#FFFFFF" />}
            onPress={() => setIsInviteModalVisible(true)}
            useTranslationText={false}
            textStyle={{ color: "#FFFFFF", fontWeight: "700", fontSize: 14 }}
            style={styles.addStudentButton}
          />
        </View>
      </View>

      <AdminTable>
        <AdminTableHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
          sortItems={SORT_ITEMS}
        />

        <AdminRosterManagementTable
          students={students}
          onResendInvite={handleResendInvite}
          onRemoveFromCourse={handleRemoveFromCourse}
          loading={loading}
        />

        {totalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </AdminTable>

      <InviteStudentModalContainer
        visible={isInviteModalVisible}
        onClose={() => setIsInviteModalVisible(false)}
        onSubmit={handleInviteStudents}
        isSubmitting={isSubmitting}
      />
      <CustomModal
        visible={modalState.visible}
        onRequestClose={() => setModalState((prev) => ({ ...prev, visible: false }))}
        showCloseButton
        title={modalState.title}>
        <CustomText
          text={modalState.message}
          size="m"
          style={{ textAlign: "center", marginVertical: 16, color: modalState.color }}
          useTranslationText={false}
        />
      </CustomModal>

      <CustomModal
        visible={removeModalState.visible}
        onRequestClose={() => setRemoveModalState({ visible: false, studentId: "", studentName: "" })}
        showCloseButton
        title="Remove Student from Course">
        <View style={{ paddingVertical: 16 }}>
          <CustomText
            text={`Are you sure you want to remove ${removeModalState.studentName} from this course?`}
            size="m"
            style={{ textAlign: "center", marginBottom: 16, color: "#111827" }}
            useTranslationText={false}
          />
          <CustomText
            text="This action cannot be undone. The student will lose access to course materials and activities immediately."
            size="s"
            style={{ textAlign: "center", marginBottom: 24, color: "#6B7280" }}
            useTranslationText={false}
          />
          <View style={{ flexDirection: "row", gap: 12, justifyContent: "center" }}>
            <CustomButton
              text="Cancel"
              styleType="secondary"
              size="m"
              onPress={() => setRemoveModalState({ visible: false, studentId: "", studentName: "" })}
              style={{ minWidth: 100 }}
            />
            <CustomButton
              text="Remove Student"
              styleType="danger"
              size="m"
              onPress={confirmRemoveFromCourse}
              style={{ minWidth: 140 }}
            />
          </View>
        </View>
      </CustomModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 32,
    backgroundColor: "#FFFFFF",
  },
  buttonGroup: {
    flexDirection: "row",
    gap: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: "row",
    gap: 12,
  },
  title: {
    color: "#111827",
  },
  exportButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#04060F1A",
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    shadowColor: "#000000",
    shadowOpacity: 0.04,
    borderRadius: 8,
  },
  addStudentButton: {
    borderRadius: 8,
  },
});

export default AdminRosterManagementContainer;
